import { useState, useEffect } from 'react';

/**
 * Hook para cargar el Facebook SDK de forma lazy.
 * Solo se carga cuando el componente que lo usa se monta.
 *
 * Si `window.FB` ya existe (p.ej. el SDK se cargó en otra pantalla, o un
 * test instaló el doble), se da por listo sin exigir `REACT_APP_FB_APP_ID`.
 *
 * El `appId` se lee dentro del efecto: Dashboard remonta este hook al
 * cambiar de bot (`key={selectedBotId}`), y el script puede estar en el
 * DOM todavía cargando. Leer el env aquí también permite que un test
 * lo configure antes de montar.
 *
 * @returns {{ ready: boolean, error: string|null }}
 */
export function useFacebookSDK() {
  const [ready, setReady] = useState(() => typeof window !== 'undefined' && !!window.FB);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (window.FB) {
      setReady(true);
      return;
    }

    const appId = process.env.REACT_APP_FB_APP_ID;
    if (!appId) {
      setError('REACT_APP_FB_APP_ID no está configurado');
      return;
    }

    window.fbAsyncInit = function() {
      window.FB.init({
        appId,
        cookie: true,
        xfbml: false,
        version: 'v21.0'
      });
      setReady(true);
    };

    const alCargar = () => {
      if (window.FB) {
        setReady(true);
      }
    };

    const existingScript = document.getElementById('facebook-jssdk');
    if (existingScript) {
      if (window.FB) {
        setReady(true);
        return;
      }
      existingScript.addEventListener('load', alCargar);
      return () => existingScript.removeEventListener('load', alCargar);
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.onerror = () => setError('No se pudo cargar el SDK de Facebook');
    document.body.appendChild(script);
  }, []);

  return { ready, error };
}

export default useFacebookSDK;
